import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/auth.store'

const TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
const WARNING_MS = 25 * 60 * 1000 // warn at 25 minutes (5 min before)
const CHECK_INTERVAL_MS = 60 * 1000 // check every 60 seconds

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart'] as const
const WARNING_TOAST_ID = 'session-timeout-warning'

export function useSessionTimeout() {
  const { isAuthenticated, logout } = useAuthStore()
  const navigate = useNavigate()
  const lastActivityRef = useRef<number>(Date.now())
  const warningShownRef = useRef<boolean>(false)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    // Reset last activity to now when user becomes authenticated
    lastActivityRef.current = Date.now()
    warningShownRef.current = false

    const handleActivity = () => {
      lastActivityRef.current = Date.now()

      // Dismiss warning toast and reset warning flag on activity
      if (warningShownRef.current) {
        toast.dismiss(WARNING_TOAST_ID)
        warningShownRef.current = false
      }
    }

    // Attach activity listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    const intervalId = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current

      if (idle >= TIMEOUT_MS) {
        // Session expired — log out and redirect
        clearInterval(intervalId)
        toast.dismiss(WARNING_TOAST_ID)
        logout().finally(() => {
          navigate('/login', { replace: true })
          toast.error(
            'Your session has expired due to inactivity. Please log in again.',
            {
              duration: 6000,
            }
          )
        })
        return
      }

      if (idle >= WARNING_MS && !warningShownRef.current) {
        warningShownRef.current = true
        toast(
          'Your session will expire in 5 minutes due to inactivity. Move your mouse or press a key to stay logged in.',
          {
            id: WARNING_TOAST_ID,
            duration: 5 * 60 * 1000, // keep visible until dismissed or timeout
            icon: '⏰',
          }
        )
      }
    }, CHECK_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
      toast.dismiss(WARNING_TOAST_ID)
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [isAuthenticated, logout, navigate])
}
